import { useEffect, useRef } from 'react';

/**
 * Bring the verse card back into view when the verse changes.
 *
 * Moving to a longer verse used to leave the reader looking at the top of a
 * block whose text ran off the bottom, with a manual scroll needed every time.
 *
 * Only fires on an actual change of verse, never while someone is scrolling on
 * their own, and honours a reduced-motion preference.
 */
const useScrollToVerse = (key, { enabled = true } = {}) => {
  const ref = useRef(null);
  const previousKey = useRef(key);

  useEffect(() => {
    if (!enabled || previousKey.current === key) return;
    previousKey.current = key;

    const element = ref.current;
    if (!element) return;

    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const behavior = reduced ? 'auto' : 'smooth';

    // Wait for the new text to be laid out, otherwise the position is measured
    // against the previous verse's height.
    const frame = requestAnimationFrame(() => {
      const box = element.getBoundingClientRect();
      const viewport = window.innerHeight;

      // A card taller than the screen is aligned near the top, with a little
      // breathing room; a shorter one is centred.
      const offset = box.height > viewport - 80
        ? box.top + window.scrollY - 16
        : box.top + window.scrollY - (viewport - box.height) / 2;

      window.scrollTo({ top: Math.max(0, offset), behavior });
    });

    return () => cancelAnimationFrame(frame);
  }, [key, enabled]);

  return ref;
};

export default useScrollToVerse;
